export async function fetchStudentTrainingData(supabase, organisationId) {
  if (!organisationId) {
    return {
      data: null,
      error: new Error('Could not identify the current organisation.'),
    };
  }

  const [
    members,
    students,
    instructors,
    courses,
    syllabusVersions,
    enrolments,
    bookings,
    trainingRecordEntries,
  ] =
    await Promise.all([
      supabase
        .from('users')
        .select('id, full_name, email, is_active')
        .eq('organisation_id', organisationId),
      supabase
        .from('student_profiles')
        .select('id, user_id, status')
        .eq('organisation_id', organisationId),
      supabase
        .from('instructor_profiles')
        .select('id, user_id, status')
        .eq('organisation_id', organisationId),
      supabase
        .from('training_courses')
        .select('id, code, name, enrolment_mode, status')
        .eq('organisation_id', organisationId),
      supabase
        .from('training_syllabus_versions')
        .select('id, course_id, version_code, source_reference, status, effective_from, effective_to, published_at')
        .eq('organisation_id', organisationId),
      supabase
        .from('course_enrolments')
        .select('id, student_profile_id, course_id, syllabus_version_id, primary_instructor_profile_id, status, start_date, completion_date')
        .eq('organisation_id', organisationId),
      supabase
        .from('bookings')
        .select('id, student_user_id, instructor_user_id, course_enrolment_id, booking_type, date, time, duration, aircraft_id, tech_log_status, briefing_data')
        .eq('organisation_id', organisationId)
        .eq('booking_type', 'training'),
      supabase
        .from('training_record_entries')
        .select('id, organisation_id, course_enrolment_id, syllabus_version_id, record_type, recorded_on, booking_id, exercise_id, flight_classification, aircraft_registration, flight_duration_minutes, subject, outcome, examiner_name, credit_minutes, credit_basis, comments, assessed_at, assessor_user_id, assessor_name, instructor_profile_id, record_payload, legacy_assessment_index, supersedes_entry_id, correction_reason, created_at')
        .eq('organisation_id', organisationId)
        .order('recorded_on', { ascending: true }),
    ]);

  const failed = [
    members,
    students,
    instructors,
    courses,
    syllabusVersions,
    enrolments,
    bookings,
    trainingRecordEntries,
  ].find((result) => result.error);

  if (failed) {
    return { data: null, error: failed.error };
  }

  const instructorNotesResults = await Promise.all(
    (instructors.data || []).map(async (instructor) => {
      const result = await supabase.rpc('training_get_instructor_notes', {
        p_instructor_profile_id: instructor.id,
      });
      return { instructorId: instructor.id, ...result };
    })
  );
  const failedInstructorNotes = instructorNotesResults.find(
    (result) => result.error
  );
  if (failedInstructorNotes) {
    return { data: null, error: failedInstructorNotes.error };
  }
  const instructorNotes = Object.fromEntries(
    instructorNotesResults.map((result) => [
      String(result.instructorId),
      result.data ?? '',
    ])
  );

  const syllabusVersionIds = (syllabusVersions.data || []).map(
    (version) => version.id
  );
  let trainingExercises = [];
  let trainingExerciseItems = [];

  if (syllabusVersionIds.length > 0) {
    const exercisesResult = await supabase
      .from('training_exercises')
      .select('id, syllabus_version_id, exercise_reference, title, display_order')
      .eq('organisation_id', organisationId)
      .in('syllabus_version_id', syllabusVersionIds)
      .order('display_order', { ascending: true });

    if (exercisesResult.error) {
      return { data: null, error: exercisesResult.error };
    }

    trainingExercises = exercisesResult.data || [];
    if (trainingExercises.length > 0) {
      const itemsResult = await supabase
        .from('training_exercise_items')
        .select('id, exercise_id, item_reference, description, display_order')
        .eq('organisation_id', organisationId)
        .in('exercise_id', trainingExercises.map((exercise) => exercise.id))
        .order('display_order', { ascending: true });

      if (itemsResult.error) {
        return { data: null, error: itemsResult.error };
      }

      trainingExerciseItems = itemsResult.data || [];
    }
  }

  return {
    data: {
      members: members.data || [],
      students: students.data || [],
      instructors: instructors.data || [],
      instructorNotes,
      courses: courses.data || [],
      syllabusVersions: syllabusVersions.data || [],
      enrolments: enrolments.data || [],
      bookings: bookings.data || [],
      trainingRecordEntries: trainingRecordEntries.data || [],
      trainingExercises,
      trainingExerciseItems,
    },
    error: null,
  };
}

export async function fetchTrainingRecordEntries(
  supabase,
  organisationId,
  courseEnrolmentId
) {
  if (!organisationId || !courseEnrolmentId) {
    return {
      data: null,
      error: new Error('Select an organisation and course enrolment first.'),
    };
  }

  return supabase
    .from('training_record_entries')
    .select('id, organisation_id, course_enrolment_id, syllabus_version_id, record_type, recorded_on, booking_id, exercise_id, flight_classification, aircraft_registration, flight_duration_minutes, subject, outcome, examiner_name, credit_minutes, credit_basis, comments, assessed_at, assessor_user_id, assessor_name, instructor_profile_id, record_payload, legacy_assessment_index, supersedes_entry_id, correction_reason, created_at')
    .eq('organisation_id', organisationId)
    .eq('course_enrolment_id', courseEnrolmentId)
    .order('recorded_on', { ascending: true });
}

export async function appendTrainingRecordEntry(supabase, record) {
  if (!record?.courseEnrolmentId || !record?.recordType) {
    return {
      data: null,
      error: new Error('A course enrolment and record type are required.'),
    };
  }

  return supabase.rpc('training_append_record_entry', { p_record: record });
}

export async function fetchCompletedSoloFlightBookings(
  supabase,
  organisationId,
  studentUserId
) {
  if (!organisationId || !studentUserId) {
    return {
      data: null,
      error: new Error('The student and organisation are required to find solo flights.'),
    };
  }

  const bookingsResult = await supabase
    .from('bookings')
    .select('id, date, time, aircraft_id, booking_type, tech_log_status')
    .eq('organisation_id', organisationId)
    .eq('user_id', studentUserId)
    .eq('booking_type', 'normal')
    .eq('tech_log_status', 'completed')
    .order('date', { ascending: false });

  if (bookingsResult.error) {
    return { data: null, error: bookingsResult.error };
  }

  const bookings = bookingsResult.data || [];
  const aircraftIds = [...new Set(bookings.map((booking) => booking.aircraft_id).filter(Boolean))];
  if (aircraftIds.length === 0) return { data: [], error: null };

  const aircraftResult = await supabase
    .from('aircraft')
    .select('id, registration')
    .eq('organisation_id', organisationId)
    .in('id', aircraftIds);

  if (aircraftResult.error) {
    return { data: null, error: aircraftResult.error };
  }

  const registrations = new Map(
    (aircraftResult.data || []).map((aircraft) => [
      String(aircraft.id),
      aircraft.registration,
    ])
  );

  return {
    data: bookings.map((booking) => ({
      ...booking,
      aircraft_registration: registrations.get(String(booking.aircraft_id)) || '',
    })),
    error: null,
  };
}

export async function createStudentProfile(supabase, organisationId, userId) {
  if (!organisationId || !userId) {
    return { data: null, error: new Error('Select an existing member first.') };
  }

  return supabase
    .from('student_profiles')
    .insert({
      organisation_id: organisationId,
      user_id: userId,
      status: 'active',
    })
    .select('id, user_id, status')
    .single();
}

export async function createInstructorProfile(supabase, organisationId, userId) {
  if (!organisationId || !userId) {
    return { data: null, error: new Error('Select an existing active member first.') };
  }

  return supabase
    .from('instructor_profiles')
    .insert({
      organisation_id: organisationId,
      user_id: userId,
      status: 'active',
    })
    .select('id, user_id, status')
    .single();
}

export async function updateInstructorNotes(
  supabase,
  { organisationId, instructorProfileId, notes }
) {
  if (!organisationId || !instructorProfileId) {
    return {
      data: null,
      error: new Error('Select an instructor profile first.'),
    };
  }

  return supabase
    .from('instructor_profiles')
    .update({ notes: String(notes || '').trim() || null })
    .eq('organisation_id', organisationId)
    .eq('id', instructorProfileId)
    .select('id')
    .single();
}

export async function createCourseEnrolment(
  supabase,
  { organisationId, studentProfileId, courseId, syllabusVersionId, instructorProfileId }
) {
  if (
    !organisationId ||
    !studentProfileId ||
    !courseId ||
    !syllabusVersionId ||
    !instructorProfileId
  ) {
    return {
      data: null,
      error: new Error('Select a student, course, published syllabus and active instructor.'),
    };
  }

  return supabase
    .from('course_enrolments')
    .insert({
      organisation_id: organisationId,
      student_profile_id: studentProfileId,
      course_id: courseId,
      syllabus_version_id: syllabusVersionId,
      primary_instructor_profile_id: instructorProfileId,
      status: 'active',
    })
    .select('id')
    .single();
}

export async function publishSyllabus(
  supabase,
  { syllabusVersionId, effectiveFrom, effectiveTo }
) {
  if (!syllabusVersionId || !effectiveFrom) {
    return {
      data: null,
      error: new Error('Select a draft syllabus and its effective start date.'),
    };
  }
  if (effectiveTo && effectiveTo < effectiveFrom) {
    return {
      data: null,
      error: new Error('The effective end date must be on or after the start date.'),
    };
  }

  return supabase.rpc('training_publish_syllabus', {
    p_syllabus_version_id: syllabusVersionId,
    p_effective_from: effectiveFrom,
    p_effective_to: effectiveTo || null,
  });
}

export async function updateCourseEnrolmentInstructor(
  supabase,
  { organisationId, enrolmentId, instructorProfileId }
) {
  if (!organisationId || !enrolmentId || !instructorProfileId) {
    return {
      data: null,
      error: new Error('Select an active instructor assignment.'),
    };
  }

  return supabase
    .from('course_enrolments')
    .update({ primary_instructor_profile_id: instructorProfileId })
    .eq('organisation_id', organisationId)
    .eq('id', enrolmentId)
    .eq('status', 'active')
    .select('id, primary_instructor_profile_id')
    .single();
}
